/**
 * Portal Kit: campos de formulário com rótulo, dica e erro no lugar certo.
 *
 * Todos recebem `erro` (a primeira mensagem do campo) e ligam rótulo, dica e erro
 * ao controle por `aria-describedby`, para o leitor de tela dizer o problema ao
 * entrar no campo.
 */
import { useId, type ReactNode } from 'react';

import type { ErroDeCampo } from '@/types/portal';

import styles from './Portal.module.css';

interface BaseProps {
  rotulo: string;
  dica?: string;
  erro?: string | null;
}

function Envoltorio({
  id,
  rotulo,
  dica,
  erro,
  children,
}: BaseProps & { id: string; children: ReactNode }) {
  return (
    <div className={styles.campo}>
      <label htmlFor={id} className={styles.rotulo}>
        {rotulo}
      </label>
      {children}
      {dica && (
        <span id={`${id}-dica`} className={styles.dica}>
          {dica}
        </span>
      )}
      {erro && (
        <span id={`${id}-erro`} className={styles.erroCampo}>
          {erro}
        </span>
      )}
    </div>
  );
}

function descritoPor(id: string, dica?: string, erro?: string | null): string | undefined {
  const ids = [dica ? `${id}-dica` : '', erro ? `${id}-erro` : ''].filter(Boolean);
  return ids.length > 0 ? ids.join(' ') : undefined;
}

interface CampoTextoProps extends BaseProps {
  valor: string;
  onChange: (v: string) => void;
  placeholder?: string;
  maxLength?: number;
  disabled?: boolean;
  mono?: boolean;
  tipo?: 'text' | 'url';
}

export function CampoTexto({
  rotulo,
  valor,
  onChange,
  dica,
  erro,
  placeholder,
  maxLength,
  disabled,
  mono,
  tipo = 'text',
}: CampoTextoProps) {
  const id = useId();
  return (
    <Envoltorio id={id} rotulo={rotulo} dica={dica} erro={erro}>
      <input
        id={id}
        type={tipo}
        className={`${styles.entrada} ${erro ? styles.entradaErro : ''} ${mono ? styles.mono : ''}`}
        value={valor}
        placeholder={placeholder}
        maxLength={maxLength}
        disabled={disabled}
        aria-invalid={erro ? true : undefined}
        aria-describedby={descritoPor(id, dica, erro)}
        onChange={(e) => onChange(e.target.value)}
      />
    </Envoltorio>
  );
}

interface CampoNumeroProps extends BaseProps {
  valor: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  passo?: number;
  disabled?: boolean;
}

export function CampoNumero({
  rotulo,
  valor,
  onChange,
  dica,
  erro,
  min,
  max,
  passo,
  disabled,
}: CampoNumeroProps) {
  const id = useId();
  return (
    <Envoltorio id={id} rotulo={rotulo} dica={dica} erro={erro}>
      <input
        id={id}
        type="number"
        className={`${styles.entrada} ${erro ? styles.entradaErro : ''} ${styles.num}`}
        value={Number.isFinite(valor) ? valor : ''}
        min={min}
        max={max}
        step={passo ?? 1}
        disabled={disabled}
        aria-invalid={erro ? true : undefined}
        aria-describedby={descritoPor(id, dica, erro)}
        onChange={(e) => onChange(e.target.value === '' ? Number.NaN : Number(e.target.value))}
      />
    </Envoltorio>
  );
}

interface CampoInterruptorProps {
  rotulo: string;
  valor: boolean;
  onChange: (v: boolean) => void;
  dica?: string;
  disabled?: boolean;
}

export function CampoInterruptor({
  rotulo,
  valor,
  onChange,
  dica,
  disabled,
}: CampoInterruptorProps) {
  const id = useId();
  return (
    <div className={styles.campo}>
      <label htmlFor={id} className={styles.interruptor}>
        <input
          id={id}
          type="checkbox"
          checked={valor}
          disabled={disabled}
          aria-describedby={dica ? `${id}-dica` : undefined}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span>{rotulo}</span>
      </label>
      {dica && (
        <span id={`${id}-dica`} className={styles.dica}>
          {dica}
        </span>
      )}
    </div>
  );
}

interface CampoSelecaoProps<T extends string> extends BaseProps {
  valor: T;
  onChange: (v: T) => void;
  opcoes: { valor: T; rotulo: string }[];
  disabled?: boolean;
}

export function CampoSelecao<T extends string>({
  rotulo,
  valor,
  onChange,
  opcoes,
  dica,
  erro,
  disabled,
}: CampoSelecaoProps<T>) {
  const id = useId();
  return (
    <Envoltorio id={id} rotulo={rotulo} dica={dica} erro={erro}>
      <select
        id={id}
        className={`${styles.entrada} ${erro ? styles.entradaErro : ''}`}
        value={valor}
        disabled={disabled}
        aria-invalid={erro ? true : undefined}
        aria-describedby={descritoPor(id, dica, erro)}
        onChange={(e) => onChange(e.target.value as T)}
      >
        {opcoes.map((o) => (
          <option key={o.valor} value={o.valor}>
            {o.rotulo}
          </option>
        ))}
      </select>
    </Envoltorio>
  );
}

interface CampoCorProps extends BaseProps {
  valor: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}

/** Cor #RRGGBB: o seletor do navegador e o texto andam juntos. */
export function CampoCor({ rotulo, valor, onChange, dica, erro, disabled }: CampoCorProps) {
  const id = useId();
  const valida = /^#[0-9a-fA-F]{6}$/.test(valor);
  return (
    <Envoltorio id={id} rotulo={rotulo} dica={dica} erro={erro}>
      <div className={styles.corLinha}>
        <input
          type="color"
          aria-label={`${rotulo} (seletor)`}
          className={styles.entradaCor}
          value={valida ? valor : '#000000'}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
        />
        <input
          id={id}
          type="text"
          className={`${styles.entrada} ${styles.mono} ${erro ? styles.entradaErro : ''}`}
          value={valor}
          maxLength={7}
          disabled={disabled}
          aria-invalid={erro ? true : undefined}
          aria-describedby={descritoPor(id, dica, erro)}
          onChange={(e) => onChange(e.target.value)}
        />
      </div>
    </Envoltorio>
  );
}

/** Lista de erros do cartão: tudo o que impede salvar, de uma vez, para o leitor de tela. */
export function ListaDeErros({ erros, titulo }: { erros: ErroDeCampo[]; titulo?: string }) {
  if (erros.length === 0) return null;
  return (
    <div role="alert" className={styles.avisoCrit}>
      <span className={styles.avisoTitulo}>{titulo ?? 'Corrija antes de salvar'}</span>
      <ul className={styles.errosLista}>
        {erros.map((e, i) => (
          <li key={`${e.campo}-${i}`}>{e.mensagem}</li>
        ))}
      </ul>
    </div>
  );
}
